<template>
  <!-- Overview tab of a third party: its contracts, where it takes our data and the company (US 9371) -->
  <div v-if="summary" class="sp-overview" qid="vendor-overview">
    <div class="sp-main">
      <!-- In short -->
      <section class="v2-card sp-sec cp-short" qid="vendor-in-short">
        <div class="cp-short__body">
          <font-awesome-icon icon="sparkles" class="cp-short__icon"/>
          <div>
            <small class="cp-short__label">{{ $t('contract_page.short.title') }}</small>
            <p class="cp-short__text">
              <span v-for="(sentence, index) in sentences" :key="index" :class="{ 'is-warn': sentence.warn }">{{ sentence.text }} </span>
            </p>
          </div>
        </div>
      </section>

      <!-- Contracts with this third party -->
      <section class="v2-card sp-sec" qid="vendor-contracts">
        <div class="sp-sec__head">
          <font-awesome-icon icon="file-alt"/>
          <h3>{{ $t('vendor_page.contracts.title', { name: vendor.name }) }}</h3>
          <span class="sp-sec__state">{{ $tc('vendor_hero.contracts', summary.contracts.length, { count: summary.contracts.length }) }}</span>
        </div>
        <div v-for="contract in summary.contracts" :key="contract.id" class="sp-kv">
          <div class="sp-kv__k">{{ contract.roles.length ? contract.roles.map(role).join(', ') : $t('contract_page.agreement.no_role') }}</div>
          <div class="sp-kv__v">
            <div class="sp-contract">
              <div class="sp-contract__top">
                <span class="sp-covers" :class="'is-' + coversGroup(contract.covers)">{{ contract.covers ? $t('systems_overview.covers.' + contract.covers) : $t('systems_overview.covers.none') }}</span>
                <router-link :to="{ name: 'contracts.view', params: { slug: contract.slug } }" class="sp-contract__name">{{ contract.name }}</router-link>
                <span v-if="contract.ended_at" class="sp-badge is-ended" qid="vendor-contract-ended"><font-awesome-icon icon="archive"/>{{ $t('contract_end.ended_on', { date: endedOn(contract.ended_at) }) }}</span>
              </div>
              <div class="sp-contract__facts">
                <template v-if="modules.gdpr">
                  <span v-if="contract.data_processor_agreement === 'Yes'" class="sp-badge is-ok"><font-awesome-icon icon="check"/>{{ $t('system_page.where.dpa') }}</span>
                  <span v-else-if="contract.data_processor_agreement === 'No'" class="sp-badge is-warn"><font-awesome-icon icon="exclamation-triangle"/>{{ $t('system_page.where.no_dpa') }}</span>
                  <span v-if="contract.countries.length" class="sp-chip">{{ contract.countries.map(country => country.name).join(', ') }}</span>
                  <span v-if="contract.sub_processors" class="sp-chip">{{ $tc('system_page.where.sub_processors', contract.sub_processors, { count: contract.sub_processors }) }}</span>
                </template>
                <router-link v-for="system in contract.systems.slice(0, 4)"
                             :key="system.id"
                             :to="{ name: 'systems.view', params: { slug: system.slug } }"
                             class="sp-chip cp-chip-sys"><font-awesome-icon icon="desktop"/> {{ system.name }}</router-link>
                <span v-if="contract.systems.length > 4" class="sp-muted">+{{ contract.systems.length - 4 }}</span>
              </div>
            </div>
          </div>
        </div>
        <div v-if="!summary.contracts.length" class="sp-kv is-missing">
          <div class="sp-kv__k"></div>
          <div class="sp-kv__v"><span class="sp-muted">{{ $t('vendor_page.contracts.none') }}</span></div>
        </div>
        <div class="sp-sec__foot">
          <router-link v-if="canCreateContract" :to="{ name: 'contracts.create' }" class="v2-link" qid="vendor-add-contract">
            <font-awesome-icon icon="plus"/>{{ $t('vendor_page.contracts.add') }}
          </router-link>
          <router-link v-if="canCreateContract && getClient.aiModule"
                       :to="{ name: 'contracts.agreement_upload', query: { vendor: vendor.slug } }"
                       class="v2-link"
                       qid="vendor-upload-agreement">
            <font-awesome-icon icon="sparkles"/>{{ $t('agreement_upload.from_vendor') }}
          </router-link>
          <button type="button" class="v2-link" @click="$emit('open-tab', 'contracts')">{{ $t('vendor_page.contracts.open') }}</button>
        </div>
      </section>

      <!-- Where it takes our data -->
      <section v-if="modules.gdpr" class="v2-card sp-sec" qid="vendor-data-goes">
        <div class="sp-sec__head">
          <font-awesome-icon icon="globe"/>
          <h3>{{ $t('vendor_page.data.title', { name: vendor.name }) }}</h3>
          <span class="sp-pips">
            <i v-for="country in summary.countries" :key="country.name" :class="{ 'is-no': needsBasis(country) }"></i>
          </span>
          <span class="sp-sec__state">{{ basisState }}</span>
        </div>
        <div v-for="country in summary.countries" :key="country.name" class="sp-kv" :class="{ 'is-missing': needsBasis(country) }">
          <div class="sp-kv__k">
            <img v-if="country.flag" :src="country.flag" height="11" class="cp-flag" alt="">{{ country.name }}
          </div>
          <div class="sp-kv__v">
            <span class="sp-muted">{{ country.via.join(', ') }}</span>
            <template v-if="country.eea === false">
              <span v-for="basis in country.basis" :key="basis" class="sp-badge" :class="isValidBasis(basis) ? 'is-ok' : 'is-warn'">{{ basisLabel(basis) }}</span>
              <span v-if="!country.basis.length" class="sp-badge is-warn"><font-awesome-icon icon="exclamation-triangle"/>{{ $t('vendor_page.data.no_basis') }}</span>
            </template>
            <span v-else-if="country.eea" class="sp-chip">{{ $t('vendor_page.data.eea') }}</span>
          </div>
        </div>
        <div v-if="!summary.countries.length" class="sp-kv is-missing">
          <div class="sp-kv__k"></div>
          <div class="sp-kv__v"><span class="sp-muted">{{ $t('vendor_page.data.none') }}</span></div>
        </div>
      </section>

      <!-- The company -->
      <section class="v2-card sp-sec" qid="vendor-company">
        <div class="sp-sec__head">
          <font-awesome-icon icon="building"/>
          <h3>{{ $t('vendor_page.company.title') }}</h3>
          <span class="sp-grow"></span>
          <button v-if="canEdit" type="button" class="v2-link" qid="vendor-company-edit" @click="$emit('edit')">
            <font-awesome-icon icon="pen"/>{{ $t('system_hero.edit') }}
          </button>
        </div>
        <div v-for="row in companyRows" :key="row.key" class="sp-kv" :class="{ 'is-missing': row.required && !row.value }">
          <div class="sp-kv__k">{{ row.label }}</div>
          <div class="sp-kv__v">
            <span v-if="row.value">{{ row.value }}</span>
            <span v-else class="sp-muted">{{ $t('contract_page.data.no_locations') }}</span>
          </div>
        </div>
      </section>
    </div>

    <div class="sp-side">
      <!-- Where it's used -->
      <section class="v2-card sp-side__card" qid="vendor-where-used">
        <h4><font-awesome-icon icon="link"/>{{ $t('system_page.used.title') }}</h4>
        <ul class="sp-used">
          <li v-for="item in usedItems" :key="item.key" :class="'is-' + item.module">
            <b>{{ item.count }}</b>{{ $tc(item.label, item.count) }}
          </li>
        </ul>
        <button type="button" class="v2-link sp-open" @click="$emit('open-tab', 'contracts')">
          {{ $t('vendor_page.contracts.open') }} <font-awesome-icon icon="arrow-right"/>
        </button>
      </section>

      <!-- Contracts of others it's a sub-processor on -->
      <section v-if="modules.subProcessors" class="v2-card sp-side__card" qid="vendor-sub-processor-on">
        <h4><font-awesome-icon icon="users"/>{{ $t('vendor_page.sub_on.title') }}</h4>
        <p class="sp-muted cp-side-intro">{{ $t('vendor_page.sub_on.intro', { name: vendor.name }) }}</p>
        <div v-for="contract in summary.sub_processor_on" :key="contract.id" class="sp-row">
          <router-link :to="{ name: 'contracts.view', params: { slug: contract.slug } }">{{ contract.name | truncate(36) }}</router-link>
          <span class="sp-muted">{{ contract.countries.map(country => country.name).join(', ') }}</span>
        </div>
        <span v-if="!summary.sub_processor_on.length" class="sp-muted">{{ $t('vendor_page.sub_on.none') }}</span>
        <button v-if="summary.counts.sub_processor_on > summary.sub_processor_on.length" type="button" class="v2-link sp-open" @click="$emit('open-tab', 'sub-processors')">
          {{ $t('vendor_page.sub_on.all', { count: summary.counts.sub_processor_on }) }} <font-awesome-icon icon="arrow-right"/>
        </button>
      </section>
    </div>
  </div>

  <!-- While the overview loads: the outline of the two columns, so the page doesn't jump -->
  <div v-else class="sp-overview" aria-hidden="true">
    <div class="sp-main">
      <div class="v2-card sp-sec cp-loading"><span class="sx-skel is-long"></span><span class="sx-skel is-short"></span></div>
      <div class="v2-card sp-sec cp-loading"><span class="sx-skel is-title"></span><span class="sx-skel is-long"></span></div>
    </div>
    <div class="sp-side">
      <div class="v2-card sp-side__card cp-loading"><span class="sx-skel is-short"></span><span class="sx-skel is-long"></span></div>
    </div>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import {isValidBasis, basisLabel} from '@/util/transferBasis.js';
  import {endedOn} from '@/util/contractEnd.js';

  export default {
    name: 'VendorOverviewTab',
    props: {
      vendor: { type: Object, required: true }
    },
    data() {
      return {
        summary: null
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      modules() {
        let client = this.getClient;
        return {
          gdpr: !!client.gdprModule,
          subProcessors: !!client.gdprAdvancedModule,
          systems: !!client.systemModule,
          activities: !!client.processingActivitiesModule,
          risk: !!client.riskModule
        };
      },
      canEdit() {
        return this.isGranted([this.permissions.THIRD_PARTY_EDIT, this.permissions.THIRD_PARTY_EDIT_OWN], this.vendor)
          && (!this.getClient.parent || this.vendor.partition);
      },
      canCreateContract() {
        return this.isGranted(this.permissions.THIRD_PARTY_CREATE);
      },
      basisState() {
        if (!this.summary.countries.length) {
          return this.$t('contract_page.data.no_locations');
        }
        let missing = this.summary.countries.filter(country => this.needsBasis(country)).length;
        return missing ? this.$tc('vendor_page.data.missing_basis', missing, { count: missing }) : this.$t('system_hero.all_filled');
      },
      companyRows() {
        let vendor = this.vendor;
        let place = [vendor.postal_code, vendor.city].filter(Boolean).join(' ');
        return [
          { key: 'name', label: this.$t('maintenance.third_parties.create.name_label'), value: vendor.name },
          { key: 'address', label: this.$t('administration.clients.create.street_label'), value: vendor.address, required: true },
          { key: 'city', label: this.$t('administration.clients.create.city_label'), value: place, required: true },
          { key: 'state', label: this.$t('administration.clients.create.state_label'), value: vendor.state },
          { key: 'country', label: this.$t('administration.clients.create.country_label'), value: vendor.country, required: true },
          { key: 'company_number', label: this.$t('administration.clients.create.cvr_label'), value: vendor.company_number, required: true }
        ];
      },
      usedItems() {
        let counts = this.summary.counts;
        let modules = this.modules;
        return [
          { key: 'contracts', label: 'vendor_page.used.contracts', count: counts.contracts, module: 'gdpr', show: true },
          { key: 'systems', label: 'contract_page.used.systems', count: counts.systems, module: 'gdpr', show: modules.systems },
          { key: 'activities', label: 'contract_page.used.activities', count: counts.activities, module: 'gdpr', show: modules.activities },
          { key: 'sub_processors', label: 'contract_page.used.sub_processors', count: counts.sub_processors, module: 'gdpr', show: modules.subProcessors },
          { key: 'sub_processor_on', label: 'vendor_page.used.sub_processor_on', count: counts.sub_processor_on, module: 'gdpr', show: modules.subProcessors },
          { key: 'risks', label: 'contract_page.used.risks', count: counts.risks, module: 'grc', show: modules.risk }
        ].filter(item => item.show);
      },
      // The third party in plain sentences, only from what's filled in
      sentences() {
        let list = [];
        let counts = this.summary.counts;
        let contracts = this.summary.contracts;
        if (!contracts.length) {
          list.push({ text: this.$t('vendor_page.short.no_contracts', { name: this.vendor.name }), warn: !counts.sub_processor_on });
          if (counts.sub_processor_on) {
            list.push({ text: this.$tc('vendor_page.short.only_sub_processor', counts.sub_processor_on, { count: counts.sub_processor_on }) });
          }
          return list;
        }
        list.push({ text: this.$t('vendor_page.short.contracts', {
          name: this.vendor.name,
          contracts: this.$tc('vendor_hero.contracts', contracts.length, { count: contracts.length }),
          systems: this.$tc('contract_page.short.systems', counts.systems, { count: counts.systems }),
          activities: this.$tc('contract_page.short.activities', counts.activities, { count: counts.activities })
        }) });
        if (this.modules.gdpr) {
          let withDpa = contracts.filter(contract => contract.data_processor_agreement === 'Yes').length;
          let processors = contracts.filter(contract => contract.roles.includes('Data processor')).length;
          list.push({ text: this.$t('vendor_page.short.dpa', { count: withDpa, total: contracts.length }), warn: withDpa < processors });
          let inside = this.summary.countries.filter(country => country.eea).map(country => country.name);
          let outside = this.summary.countries.filter(country => country.eea === false).map(country => country.name);
          if (outside.length) {
            let missing = this.summary.countries.filter(country => this.needsBasis(country)).length;
            list.push({ text: inside.length
              ? this.$t('vendor_page.short.outside', { inside: inside.join(', '), outside: outside.join(', ') })
              : this.$t('vendor_page.short.only_outside', { outside: outside.join(', ') }), warn: missing > 0 });
          } else if (inside.length) {
            list.push({ text: this.$t('vendor_page.short.inside', { inside: inside.join(', ') }) });
          } else {
            list.push({ text: this.$t('contract_page.short.no_locations'), warn: true });
          }
        }
        return list;
      }
    },
    watch: {
      'vendor.id': function () {
        this.load();
      },
      'vendor.updated_at': function () {
        this.load();
      }
    },
    methods: {
      endedOn: endedOn,
      load() {
        let self = this;
        piincHttp.get('vendors/' + this.vendor.slug + '/overview', { params: { client: this.getClient.slug } }).then(function(response) {
          self.summary = response.data;
          self.$emit('summary', response.data);
        }, function() {});
      },
      role(value) {
        let key = 'maintenance.third_parties.processing_role.' + this.$options.filters.slugify(value);
        return this.$te(key) ? this.$t(key) : value;
      },
      value(group, value) {
        let key = 'maintenance.third_parties.' + group + '.' + this.$options.filters.slugify(value);
        return this.$te(key) ? this.$t(key) : value;
      },
      coversGroup(covers) {
        if (!covers) {
          return 'unsorted';
        }
        return ['software_hosting', 'hosting', 'licence'].includes(covers) ? 'runs_on' : 'access';
      },
      // Outside the EU/EEA without a valid transfer basis on every contract that sends data there
      needsBasis(country) {
        return country.eea === false && (!country.basis.length || !country.basis.every(isValidBasis));
      },
      isValidBasis(basis) {
        return isValidBasis(basis);
      },
      basisLabel(basis) {
        return basisLabel(this, basis);
      }
    },
    created() {
      if (this.vendor.id) {
        this.load();
      }
    }
  }
</script>
